import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Observable, finalize, map } from 'rxjs';
import { ShadeProxyService } from '../../../proxies/item/shade.proxy.service';
import { RecipeProxyService } from '../../../proxies/item/recipe.proxy.service';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Shade } from '../../../models/shade/shade';
import { LabDip } from '../../../models/lab-dip/lab-dip';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { ManufacturerLotNumberKey, ManufacturerLotNumberFormGroup, Recipe } from '../../../models/recipe/recipe';
import { Process, ProcessSnapshot, StepIdsWithOrder } from '../../../models/engineering/process';
import { Step } from '../../../models/engineering/step';
import { ItemConsumableDetails, ItemDetailFormValue } from '../../../models/engineering/item-consumable-details';
import { IngredientUnit } from '../../../models/engineering/enum/IngredientUnit.enum';
import { ScalingMethod } from '../../../models/engineering/enum/ScalingMethod.enum';
import { BreadcrumbService, Note, UserLite, ModalService, NotesSidebarComponent, NotesModalData } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { TestLabDipAdminGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-recipe-details',
  templateUrl: './recipe-details.component.html',
  styleUrl: './recipe-details.component.scss',
})
export class RecipeDetailsComponent implements OnInit {
  private readonly shadeProxyService = inject(ShadeProxyService);
  private readonly recipeProxyService = inject(RecipeProxyService);
  private readonly labDipProxyService = inject(LabDipProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userAuthService = inject(UserAuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly isLoading = signal(0);
  readonly shadeOptions = signal<{ label: string; value: string }[]>([]);
  readonly selectedLabDip = signal<LabDip | null>(null);
  readonly manufacturerLotNumbersMap = new Map<number, string[]>();
  readonly filteredLotNumbersMap = new Map<number, string[]>();

  canEdit = computed(() => {
    if (AuthService.hasRole(TestLabDipAdminGroup.TestLabDipAdmin)) {
      return true;
    }
    return false;
  });

  recipeId = signal<string | null>(null);
  isEditMode = signal(false);
  isCopyMode = signal(false);
  notes = signal<Note[]>([]);
  userLite = signal<UserLite | null>(null);
  private returnTo = signal<string | null>(null);
  private labDipId = signal<string | null>(null);

  readonly labDipReference = new FormControl<LabDip | null>(null, [Validators.required]);
  readonly recipeNumber = new FormControl<string>('', { nonNullable: true });
  readonly liquorRatio = new FormControl<number | null>(null, [Validators.required, Validators.min(0.1)]);
  readonly shadeReference = new FormControl<string | null>(null, [Validators.required]);
  readonly processes = new FormControl<Process[]>([], { nonNullable: true });

  readonly formGroup = new FormGroup({
    labDipReference: this.labDipReference,
    recipeNumber: this.recipeNumber,
    liquorRatio: this.liquorRatio,
    shadeReference: this.shadeReference,
    processes: this.processes,
  });

  readonly manufacturerLotNumbers = new FormArray<FormGroup<ManufacturerLotNumberFormGroup>>([]);

  ngOnInit(): void {
    this.userLite.set(this.userAuthService.userLite);
    this.loadShades();

    // Disable form controls for non-admin users
    if (!this.canEdit()) {
      this.formGroup.disable();
      this.manufacturerLotNumbers.disable();
    }

    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      const returnTo = params.get('returnTo');
      const labDipId = params.get('labDipId');
      const isCopy = params.get('isCopy') === 'true';

      this.returnTo.set(returnTo);
      this.labDipId.set(labDipId);
      this.isCopyMode.set(isCopy);

      if (!id || isCopy) {
        this.breadcrumbService.setCurrent('Common_New');
      }

      if (id && !isCopy) {
        this.recipeId.set(id);
        this.isEditMode.set(true);
        this.loadRecipe(id);
      } else if (id && isCopy) {
        this.recipeId.set(null);
        this.isEditMode.set(false);
        this.loadRecipe(id);
      } else if (labDipId && !this.labDipReference.value) {
        this.recipeId.set(null);
        this.loadLabDipById(labDipId);
      }
    });
  }

  private incrementLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private loadShades(): void {
    this.incrementLoading();
    this.shadeProxyService
      .getAllShades()
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.shadeOptions.set(this.convertShadesToOptions(response.data));
          }
        },
      });
  }

  private loadRecipe(id: string): void {
    this.incrementLoading();
    this.recipeProxyService
      .getRecipeById(id)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.populateForm(response.data);
          }
        },
      });
  }

  private populateForm(recipe: Recipe): void {
    if (recipe.labDipReference) {
      this.loadLabDipById(recipe.labDipReference);
    }

    if (this.isCopyMode()) {
      this.recipeNumber.setValue('');
      this.notes.set([]);
    } else {
      this.recipeNumber.setValue(recipe.recipeNumber);
      this.breadcrumbService.setCurrent(recipe.recipeNumber);
      if (recipe.notes && recipe.notes.length > 0) {
        this.notes.set(recipe.notes);
      }
    }

    this.liquorRatio.setValue(recipe.liquorRatio);
    this.shadeReference.setValue(recipe.shadeReference);

    if (recipe.processSnapshots && recipe.processSnapshots.length > 0) {
      this.loadProcessesFromSnapshots(recipe.processSnapshots);
    }

    this.manufacturerLotNumbers.clear();
    if (recipe.manufacturerLotNumberKeys && recipe.manufacturerLotNumberKeys.length > 0) {
      recipe.manufacturerLotNumberKeys.forEach((mln, index) => {
        this.manufacturerLotNumbers.push(this.createManufacturerLotNumberGroup(mln, index));
      });
    }
  }

  private loadLabDipById(labDipId: string): void {
    this.incrementLoading();
    this.labDipProxyService
      .getLabDipById(labDipId)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.labDipReference.setValue(response.data);
            this.selectedLabDip.set(response.data);
          }
        },
      });
  }

  public fetchLabDips = (query: string): Observable<LabDip[]> =>
    this.labDipProxyService.searchLabDips({ labDipNumber: query }).pipe(map((response) => response.data?.items || []));

  getLabDipLabel(labDip: LabDip): string {
    return labDip.labDipNumber || labDip.name || '';
  }

  private loadProcessesFromSnapshots(processSnapshots: ProcessSnapshot[]): void {
    if (!processSnapshots || processSnapshots.length === 0) {
      this.processes.setValue([]);
      return;
    }

    const processes: Process[] = processSnapshots.map((snapshot) => {
      const stepIdsWithOrder: StepIdsWithOrder[] = snapshot.steps
        ? snapshot.steps.map((stepSnap) => ({
            stepId: stepSnap.stepSnapshot.stepId,
            order: stepSnap.order,
            step: stepSnap.stepSnapshot,
          }))
        : [];

      const process: Process = {
        id: snapshot.id,
        processId: snapshot.processId,
        processTypeReference: snapshot.processTypeReference,
        name: snapshot.name,
        notes: snapshot.notes || [],
        description: snapshot.description,
        assetTypeReferences: snapshot.assetTypeReferences || [],
        itemDetails: snapshot.itemDetails || [],
        additionalParameters: snapshot.additionalParameters || [],
        stepIdsWithOrder: stepIdsWithOrder,
        showInFabricGeneralProcessRoute: snapshot.showInFabricGeneralProcessRoute,
        isSystem: snapshot.isSystem,
      };

      return process;
    });

    this.processes.setValue(processes);
  }

  private convertShadesToOptions(shades: Shade[]): { label: string; value: string }[] {
    return shades.map((shade) => ({
      label: shade.name,
      value: shade.id || '',
    }));
  }

  onSave(): void {
    if (this.formGroup.invalid) {
      return;
    }

    const labDip = this.labDipReference.value;
    if (!labDip || !labDip.id) {
      return;
    }

    const manufacturerKeys: ManufacturerLotNumberKey[] = this.manufacturerLotNumbers.controls.map((control) => {
      const value = control.value;
      const manufacturer = value.manufacturerReference as Business | null;

      return {
        fiberCode: value.fiberCode || manufacturer?.code || '',
        manufacturerReference: manufacturer?.id || '',
        manufacturerLotNumber: value.manufacturerLotNumber || '',
        dateAdded: value.dateAdded || new Date(),
        addedBy: value.addedBy || this.userLite()?.username || '',
      };
    });

    const recipeData: Recipe = {
      id: this.recipeId() || undefined,
      recipeNumber: this.recipeNumber.value,
      labDipReference: labDip.id,
      liquorRatio: this.liquorRatio.value!,
      shadeReference: this.shadeReference.value!,
      processSnapshots: this.convertProcessesToSnapshots(this.processes.value),
      manufacturerLotNumberKeys: manufacturerKeys,
      notes: this.notes(),
      createdBy: '',
      createdDate: new Date(),
      modifiedBy: '',
      modifiedDate: new Date(),
      isForProductDevelopment: false,
    };

    const isEdit = this.isEditMode() && !this.isCopyMode();
    const currentRecipeId = this.recipeId();

    this.incrementLoading();
    if (isEdit && currentRecipeId) {
      this.recipeProxyService
        .updateRecipe(currentRecipeId, recipeData)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            this.navigateBack(response.data?.id);
          },
        });
    } else {
      this.recipeProxyService
        .createRecipe(recipeData)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            this.navigateBack(response.data?.id);
          },
        });
    }
  }

  private convertProcessesToSnapshots(processes: Process[]): ProcessSnapshot[] {
    return processes.map((process) => {
      const stepSnapshots: { order: number; stepSnapshot: Step }[] = [];

      if (process.stepIdsWithOrder && process.stepIdsWithOrder.length > 0) {
        process.stepIdsWithOrder.forEach((swo) => {
          if (swo.step) {
            const cleanedStep = this.cleanStepForSnapshot(swo.step);
            stepSnapshots.push({
              order: swo.order,
              stepSnapshot: cleanedStep,
            });
          }
        });
      }

      const cleanedItemDetails = this.cleanItemDetailsForSnapshot(process.itemDetails || []);

      const processSnapshot: ProcessSnapshot = {
        ...process,
        itemDetails: cleanedItemDetails,
        steps: stepSnapshots,
      };

      return processSnapshot;
    });
  }

  private cleanStepForSnapshot(step: Step): Step {
    const cleanedItemDetails = this.cleanItemDetailsForSnapshot(step.itemDetails || []);
    return {
      ...step,
      itemDetails: cleanedItemDetails,
    };
  }

  private cleanItemDetailsForSnapshot(itemDetails: (ItemConsumableDetails | ItemDetailFormValue)[]): ItemConsumableDetails[] {
    return itemDetails.map((item) => {
      if ('item' in item && item.item && item.item.itemCode) {
        return {
          itemCode: item.item.itemCode,
          isPreferred: item.isPreferred,
          ingredientUnit: item.ingredientUnit ?? IngredientUnit.Percent,
          scalingMethod: item.scalingMethod ?? ScalingMethod.ByWeight,
          quantity: item.quantity ?? 0,
        };
      }
      return item as ItemConsumableDetails;
    });
  }

  onCancel(): void {
    this.navigateBack();
  }

  private navigateBack(recipeId?: string): void {
    const returnTo = this.returnTo();
    const labDipId = this.labDipId();

    if (returnTo === 'recipe-container' && labDipId) {
      const queryParams: { labDipId: string; recipeId?: string } = { labDipId };
      if (recipeId) {
        queryParams.recipeId = recipeId;
      }
      this.router.navigate(['/recipe-container'], { queryParams });
    } else {
      this.router.navigate(['/lab-dips']);
    }
  }

  public fetchManufacturers = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({
        name: query,
        businessTypes: [BusinessEntityType.Manufacturer],
      })
      .pipe(map((response) => response.data?.items || []));

  getManufacturerLabel(manufacturer: Business): string {
    return manufacturer.name || '';
  }

  onManufacturerSelected(rowIndex: number, event: { value: Business }): void {
    const manufacturer = event.value as Business;
    if (!manufacturer) return;

    const row = this.manufacturerLotNumbers.at(rowIndex);
    if (row) {
      const lotNumbers = manufacturer.lotNumbers || [];
      row.get('fiberCode')?.setValue(manufacturer.code || '');
      this.manufacturerLotNumbersMap.set(rowIndex, lotNumbers);
      // Inicializar con los primeros 20 resultados
      this.filteredLotNumbersMap.set(rowIndex, lotNumbers.slice(0, 20));
      row.get('manufacturerLotNumber')?.setValue(null);
    }
  }

  getFilteredLotNumbers(rowIndex: number): string[] {
    return this.filteredLotNumbersMap.get(rowIndex) || [];
  }

  filterLotNumbers(event: AutoCompleteCompleteEvent, rowIndex: number): void {
    const query = (event.query || '').toLowerCase();
    const allLotNumbers = this.manufacturerLotNumbersMap.get(rowIndex) || [];

    if (!query) {
      // Limitar a los primeros 20 resultados cuando no hay búsqueda
      this.filteredLotNumbersMap.set(rowIndex, allLotNumbers.slice(0, 20));
    } else {
      const filtered = allLotNumbers.filter((lotNumber) => lotNumber.toLowerCase().includes(query));
      this.filteredLotNumbersMap.set(rowIndex, filtered);
    }
  }

  createManufacturerLotNumberGroup(data?: ManufacturerLotNumberKey, index?: number): FormGroup<ManufacturerLotNumberFormGroup> {
    if (data?.manufacturerReference && index !== undefined) {
      this.loadManufacturerForEdit(data.manufacturerReference, index);
    }

    return new FormGroup<ManufacturerLotNumberFormGroup>({
      fiberCode: new FormControl<string | null>(data?.fiberCode || null, [Validators.required]),
      manufacturerReference: new FormControl<Business | null>(null, [Validators.required]),
      manufacturerLotNumber: new FormControl<string | null>(data?.manufacturerLotNumber || null, [Validators.required]),
      dateAdded: new FormControl<Date | null>(data?.dateAdded || null),
      addedBy: new FormControl<string | null>(data?.addedBy || null),
    });
  }

  private loadManufacturerForEdit(manufacturerId: string, index: number): void {
    this.businessProxyService.searchBatchIds([manufacturerId]).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          const manufacturer = response.data[0];
          const lotNumbers = manufacturer.lotNumbers || [];
          this.manufacturerLotNumbers.at(index)?.get('manufacturerReference')?.setValue(manufacturer);
          this.manufacturerLotNumbersMap.set(index, lotNumbers);
          this.filteredLotNumbersMap.set(index, lotNumbers.slice(0, 20));
        }
      },
    });
  }

  addManufacturerLotNumber(): void {
    const newGroup = this.createManufacturerLotNumberGroup();
    const userLite = this.userLite();

    if (userLite) {
      newGroup.get('dateAdded')?.setValue(new Date());
      newGroup.get('addedBy')?.setValue(userLite.username);
    }

    this.manufacturerLotNumbers.push(newGroup);
  }

  removeManufacturerLotNumber(index: number): void {
    if (index >= 0 && index < this.manufacturerLotNumbers.length) {
      this.manufacturerLotNumbers.removeAt(index);
    }
  }

  onNoteAdded(): void {
    // El componente csl-standard-notes ya agrega la nota al array
    // Este método existe solo para manejar el evento si es necesario
  }

  onViewMore(): void {
    this.modalService
      .open<NotesModalData>(
        NotesSidebarComponent,
        {
          modalClass: 'notes-sidebar-modal',
          position: 'right',
          closeOnVeilClick: true,
        },
        {
          notes: this.notes(),
          userLite: this.userLite()!,
          viewOnly: false,
        },
      )
      .subscribe((result: unknown) => {
        const r = result as { data?: { notes: Note[] } };
        if (r?.data?.notes) {
          this.notes.set(r.data.notes);
        }
      });
  }
}
